import {
  CanAccess,
  useListContext,
  useShowContext,
  useTranslate,
} from "ra-core";
import { Link as RouterLink } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { DateField } from "@/components/admin/date-field";
import { EditButton } from "@/components/admin/edit-button";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { RecordField } from "@/components/admin/record-field";
import { SelectField } from "@/components/admin/select-field";
import { Separator } from "@/components/ui/separator";
import { Show } from "@/components/admin/show";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { findDealLabel } from "../deals/dealUtils";
import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";
import type { Client, Deal, Sale } from "../types";
import {
  CLIENT_TYPE_CHOICES,
  MARITAL_PROPERTY_REGIME_CHOICES,
  MARITAL_STATUS_CHOICES,
} from "./clientChoices";

/** No i18n key needed: an em dash is not language-specific placeholder text. */
const EMPTY = "—";

/**
 * Read-only detail view (Almikia Epic E3): create/edit forms ship in
 * TASK-022. No delete affordance for any role, direccion included — client
 * records are never hard-deleted (mirrors canAccess.ts / the database RLS).
 */
export const ClientShow = () => (
  <Show actions={<ClientShowActions />}>
    <ClientShowLayout />
  </Show>
);

const ClientShowActions = () => (
  <CanAccess resource="clients" action="edit">
    {/*
      This screen's single main action (design-system.md section 6/8): the
      next step for a client's file is to edit it, so "Editar" gets the
      gradient primary style, mirroring the lead show page (TASK-046).
    */}
    <EditButton variant="gradient" />
  </CanAccess>
);

const ClientShowLayout = () => {
  const { record, isPending } = useShowContext<Client>();

  if (isPending || !record) return null;

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <RecordField
          source="client_type"
          label="resources.clients.fields.client_type"
        >
          <SelectField source="client_type" choices={CLIENT_TYPE_CHOICES} />
        </RecordField>
        <RecordField source="full_legal_name" />
        <RecordField source="rfc" empty={EMPTY} />
        <RecordField source="curp" empty={EMPTY} />
        <RecordField
          source="birthday"
          label="resources.clients.fields.birthday"
          empty={EMPTY}
        >
          {/*
            No `options` prop: DateField's default for a date-only string
            already matches dd/mm/aaaa AND forces UTC (see date-field.tsx),
            which passing NUMERIC_DATE_FORMAT here would bypass, shifting the
            birthday back a day for negative-UTC-offset users (Mexico).
          */}
          <DateField source="birthday" empty={EMPTY} className="tabular-nums" />
        </RecordField>
        <RecordField
          source="marital_status"
          label="resources.clients.fields.marital_status"
          empty={EMPTY}
        >
          <SelectField
            source="marital_status"
            choices={MARITAL_STATUS_CHOICES}
            empty={EMPTY}
          />
        </RecordField>
        <RecordField
          source="marital_property_regime"
          label="resources.clients.fields.marital_property_regime"
          empty={EMPTY}
        >
          <SelectField
            source="marital_property_regime"
            choices={MARITAL_PROPERTY_REGIME_CHOICES}
            empty={EMPTY}
          />
        </RecordField>
        <RecordField source="children_count" empty={EMPTY} />
        <RecordField source="email" empty={EMPTY} />
        <RecordField source="phone" empty={EMPTY} />
        <RecordField source="address" empty={EMPTY} />
        <RecordField
          source="sales_id"
          label="resources.clients.fields.sales_id"
        >
          <ReferenceField<Client, Sale>
            source="sales_id"
            reference="sales"
            render={({ referenceRecord }) =>
              referenceRecord
                ? `${referenceRecord.first_name} ${referenceRecord.last_name}`
                : null
            }
          />
        </RecordField>
        <RecordField
          source="contact_id"
          label="resources.clients.fields.contact_id"
          empty={EMPTY}
        >
          <ReferenceField
            source="contact_id"
            reference="contacts"
            link="show"
            empty={EMPTY}
          />
        </RecordField>

        <Separator />

        <LinkedLeadsSection />
      </CardContent>
    </Card>
  );
};

/**
 * Leads linked to this client (Almikia Epic E3, TASK-023): a single
 * indexed query by `client_id` only — no client field value is ever sent
 * to the data provider, so this section stays PII-safe by construction.
 * Capped at 100 (a client with more linked leads than that is not expected
 * in practice) rather than paginated, keeping the section a simple list.
 */
const LinkedLeadsSection = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-base font-medium">
        {translate("resources.clients.linked_leads.title")}
      </h3>
      <ReferenceManyField
        reference="deals"
        target="client_id"
        sort={{ field: "name", order: "ASC" }}
        perPage={100}
        // No retry: surface the error/retry state right away instead of
        // silently retrying for several seconds, matching useGetMany's
        // default (ra-core/dist/dataProvider/useGetMany.js).
        queryOptions={{ retry: false }}
        empty={
          <p className="text-sm text-muted-foreground">
            {translate("resources.clients.linked_leads.empty")}
          </p>
        }
        // ReferenceManyFieldView otherwise intercepts the pending/error
        // states itself and renders nothing (its own `loading`/`error`
        // props default to undefined, not "fall through to children").
        // Disabling both here hands control to LinkedLeadsList, which
        // renders the skeleton/error UI itself — same pattern as
        // ContactShow.tsx's InfiniteListBase.
        loading={false}
        error={false}
      >
        <LinkedLeadsList />
      </ReferenceManyField>
    </div>
  );
};

const LinkedLeadsList = () => {
  const translate = useTranslate();
  const { dealStages } = useConfigurationContext();
  const { data: leads, error, isPending, refetch } = useListContext<Deal>();

  if (isPending) return <ListCardsSkeleton rows={2} />;
  if (error) {
    return (
      <ListErrorState
        message={translate("crm.common.list_error")}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="flex flex-col">
      {leads.map((lead) => (
        <RouterLink
          key={lead.id}
          to={`/deals/${lead.id}/show`}
          className="flex items-center justify-between hover:bg-muted py-2 px-2 transition-colors"
        >
          <span className="text-sm font-medium">{lead.name}</span>
          <span className="text-xs text-muted-foreground">
            {findDealLabel(dealStages, lead.stage)}
          </span>
        </RouterLink>
      ))}
    </div>
  );
};
